import moment from "moment/moment";
import {
  IconCalendarCheck,
  IconFileText,
  IconLock,
  IconMapPin,
  IconPencil,
  IconReceipt,
  IconTrendingUp,
} from "@tabler/icons-react";
import Card from "./Card";
import Text from "./Text";
import IconButton from "./IconButton";
import InfoItem from "./InfoItem";
import RentGraph, { type RentInput } from "./RentGraph";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions";
import { ChargesType, TenantLeaseType } from "../../utils/constants/tenants";
import styles from "./LeaseConditionsCard.module.css";
import type { LeaseTemplate } from "../../models/leaseTemplate";
import { formatTypeIndice } from "../../models/property";

export interface LeaseConditions extends RentInput {
  debut?: string | null;
  fin?: string | null;
  preavis?: boolean;
  deposit?: number;
  type_charge?: string | null;
  lease_type?: string;
  day_payment?: number;
  payment_periods_from_start_date?: boolean;
  last_revision_date?: string | null;
  is_type_quittance?: boolean;
  is_indemnite_occupation?: boolean;
  echeance_paiement?: string;
  type?: string;
  zone_tendue?: boolean;
  type_indice?: string | null;
  type_irl?: string | null;
  loyer_reference?: boolean;
  montant_loyer_ref?: number;
  montant_loyer_ref_maj?: number;
  complement_loyer?: number;
}

interface LeaseConditionsCardProps {
  leaseConditions: LeaseConditions;
  leaseTemplate?: LeaseTemplate;
  onEdit?: () => void;
}

function getDayPaymentValue(leaseConditions: LeaseConditions): string {
  if (!leaseConditions.day_payment) return "Non renseigné";
  let suffix = "";
  if (leaseConditions.payment_recurrence === 3) suffix = " (Trimestriel";
  else if (leaseConditions.payment_recurrence === 6) suffix = " (Semestriel";
  else if (leaseConditions.payment_recurrence === 12) suffix = " (Annuel";
  if (suffix) {
    if (leaseConditions.payment_periods_from_start_date) {
      suffix += ", périodes calées sur le début du bail";
    }
    suffix += ")";
  }
  return `Le ${leaseConditions.day_payment} du mois${suffix}`;
}

function getLeaseTypeValue(
  leaseConditions: LeaseConditions,
  leaseTemplate?: LeaseTemplate,
): string {
  if (leaseTemplate) return leaseTemplate.nom;
  if (leaseConditions.lease_type) return `Bail ${leaseConditions.lease_type}`;
  return "Non renseigné";
}

function documentTypeLabel(leaseConditions: LeaseConditions) {
  const base = leaseConditions.is_type_quittance ? "Quittance" : "Facture";
  return leaseConditions.is_indemnite_occupation
    ? `${base} d'indemnité d'occupation`
    : base;
}

export default function LeaseConditionsCard({
  leaseConditions,
  leaseTemplate,
  onEdit,
}: LeaseConditionsCardProps) {
  const showDeposit = leaseConditions.lease_type !== TenantLeaseType.MOBILITE;

  return (
    <Card>
      <div className={styles.header}>
        <Text variant="deca" weight="bold" as="h2">
          Paramètres du bail
        </Text>
        {onEdit && (
          <IconButton
            onClick={onEdit}
            icon={IconPencil}
            label="Modifier"
            requiresWrite
          />
        )}
      </div>
      <RentGraph
        title="Loyer"
        tenant={leaseConditions}
        className={styles.rentGraph}
      />
      <div className={styles.content}>
        {leaseConditions.debut !== null && (
          <InfoItem
            icon={IconCalendarCheck}
            label="Début du bail"
            value={
              leaseConditions.debut
                ? moment(leaseConditions.debut).format("DD MMMM YYYY")
                : "Non renseigné"
            }
          />
        )}
        {leaseConditions.fin !== null && (
          <InfoItem
            icon={IconCalendarCheck}
            label={
              leaseConditions.preavis
                ? "Date de fin (préavis déposé)"
                : "Date d'échéance du bail"
            }
            value={
              leaseConditions.fin
                ? moment(leaseConditions.fin).format("DD MMMM YYYY")
                : "Non renseigné"
            }
          />
        )}
        {showDeposit && (
          <InfoItem
            icon={IconLock}
            label="Dépôt de garantie"
            value={
              currencyFormatter(leaseConditions.deposit) || "Non renseigné"
            }
          />
        )}
        <InfoItem
          icon={IconFileText}
          label="Type de bail"
          value={getLeaseTypeValue(leaseConditions, leaseTemplate)}
        />
        {leaseConditions.type_charge && (
          <InfoItem
            icon={IconReceipt}
            label="Charges"
            value={
              leaseConditions.type_charge === ChargesType.FORFAIT
                ? "Forfait de charges"
                : "Provision sur charges"
            }
          />
        )}
        <InfoItem
          icon={IconCalendarCheck}
          label="Jour de paiement du loyer"
          value={getDayPaymentValue(leaseConditions)}
        />
        {leaseConditions.last_revision_date !== null && (
          <InfoItem
            icon={IconCalendarCheck}
            label="Dernière révision de loyer"
            value={
              leaseConditions.last_revision_date
                ? moment(leaseConditions.last_revision_date).format(
                    "DD MMMM YYYY",
                  )
                : "Non renseigné"
            }
          />
        )}
        {leaseConditions.is_type_quittance !== undefined && (
          <InfoItem
            icon={IconFileText}
            label="Type de document"
            value={documentTypeLabel(leaseConditions)}
          />
        )}
        {leaseConditions.echeance_paiement && (
          <InfoItem
            icon={IconCalendarCheck}
            label="Échéance de paiement"
            value={leaseConditions.echeance_paiement}
          />
        )}
        {leaseConditions.type === "habitation" && (
          <InfoItem
            icon={IconMapPin}
            label="Type de zone"
            value={
              leaseConditions.zone_tendue ? "Zone tendue" : "Zone non tendue"
            }
          />
        )}
        {(leaseConditions.type_irl ||
          (leaseConditions.type && leaseConditions.type !== "habitation")) && (
          <InfoItem
            icon={IconTrendingUp}
            label="Indice de révision du loyer"
            value={formatTypeIndice(
              leaseConditions.type_irl ?? leaseConditions.type_indice,
            )}
          />
        )}
      </div>
      {leaseConditions.loyer_reference && (
        <div className={styles.referenceSection}>
          <div className={styles.referenceRow}>
            <Text variant="deci" weight="medium" color="muted">
              Loyer de référence
            </Text>
            <Text variant="deci" weight="bold">
              {currencyFormatter(leaseConditions.montant_loyer_ref)}{" "}
              <Text as="span" variant="micro" weight="medium" color="muted">
                /m²
              </Text>
            </Text>
          </div>
          <div className={styles.referenceRow}>
            <Text variant="deci" weight="medium" color="muted">
              Loyer majoré
            </Text>
            <Text variant="deci" weight="bold">
              {currencyFormatter(leaseConditions.montant_loyer_ref_maj)}{" "}
              <Text as="span" variant="micro" weight="medium" color="muted">
                /m²
              </Text>
            </Text>
          </div>
          <div className={styles.referenceRow}>
            <Text variant="deci" weight="medium" color="muted">
              Complément de loyer
            </Text>
            <Text variant="deci" weight="bold">
              {currencyFormatter(leaseConditions.complement_loyer)}
            </Text>
          </div>
        </div>
      )}
    </Card>
  );
}
